import type { AllergySeverity, AllergyVM, DiagnosisVM } from './types';

/**
 * Wire rows for the diagnoses/allergies cards. Structural subsets of the
 * generated `DiagnosisInfo*` / `AllergyInfo*` types — declared locally so the
 * mapper accepts every generated variant (the list and mutation payloads use
 * distinct-but-identical generated names).
 */
export interface DiagnosisRow {
  diagnosis_id: string;
  name: string;
  code?: string | null;
  date_added?: string | null;
  end_date?: string | null;
  is_active?: boolean;
  /** Suggestion-workflow state — distinct from clinical is_active. */
  status?: string;
}

export interface AllergyRow {
  allergy_id: string;
  allergen: string;
  reaction?: string | null;
  severity?: string | null;
  date_added?: string | null;
  onset_date?: string | null;
  is_active?: boolean;
  /** Suggestion-workflow state — allergies only know accepted/suggested. */
  status?: string;
}

const SEVERITIES: readonly AllergySeverity[] = [
  'mild',
  'moderate',
  'severe',
  'life-threatening',
];

const toSeverity = (value: string | null | undefined): AllergySeverity | undefined =>
  SEVERITIES.includes(value as AllergySeverity) ? (value as AllergySeverity) : undefined;

// Parity with the legacy blocks' filter predicate: a row is active unless
// is_active is explicitly false.
const isActive = (row: { is_active?: boolean }): boolean => row.is_active !== false;

// Dismissed AI proposals (status='rejected') never render — matching the V2
// clinical-chart surface. The legacy V1 blocks showed them as normal rows,
// which read as accepted; that was a bug, not a behavior to preserve. Applies
// to BOTH mappers: an allergy row carries the same suggestion-workflow status.
export const mapDiagnosesToVM = (rows: DiagnosisRow[]): DiagnosisVM[] =>
  rows
    .filter((row) => row.status !== 'rejected')
    .map((row) => ({
      id: row.diagnosis_id,
      name: row.name,
      code: row.code ?? undefined,
      dateAdded: row.date_added ?? null,
      endDate: row.end_date ?? null,
      isActive: isActive(row),
      isSuggested: row.status === 'suggested',
    }));

export const mapAllergiesToVM = (rows: AllergyRow[]): AllergyVM[] =>
  rows
    .filter((row) => row.status !== 'rejected')
    .map((row) => ({
      id: row.allergy_id,
      allergen: row.allergen,
      reaction: row.reaction ?? undefined,
      severity: toSeverity(row.severity),
      onsetDate: row.onset_date ?? null,
      isActive: isActive(row),
      isSuggested: row.status === 'suggested',
    }));
